// Componente para ativar notificações push e enviar WhatsApp
import React, { useState } from 'react';
import { Bell, BellOff, MessageCircle, Send, Loader2, Check, X } from 'lucide-react';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import usePushNotifications from '../../hooks/usePushNotifications';

const API_URL = window.location.origin;

// Componente para ativar Push Notifications
export function PushNotificationButton({ responsavelId, responsavelNome }) {
  const {
    isSupported,
    permission,
    isSubscribed,
    isLoading,
    error,
    subscribe,
    unsubscribe
  } = usePushNotifications(responsavelId);

  const handleToggle = async () => {
    if (isSubscribed) {
      await unsubscribe();
      toast.success('Notificações desativadas');
    } else {
      const result = await subscribe();
      if (result) {
        toast.success('Notificações ativadas! Você receberá alertas quando o aluno for chamado.');
      } else if (permission === 'denied') {
        toast.error('Permissão negada. Habilite notificações nas configurações do navegador.');
      }
    }
  };

  if (!isSupported) {
    return (
      <Button variant="outline" disabled size="sm">
        <BellOff className="h-4 w-4 mr-2" />
        Não suportado
      </Button>
    );
  }

  return (
    <Button
      variant={isSubscribed ? "default" : "outline"}
      size="sm"
      onClick={handleToggle}
      disabled={isLoading}
      className={isSubscribed ? "bg-green-600 hover:bg-green-700" : ""}
    >
      {isLoading ? (
        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
      ) : isSubscribed ? (
        <Bell className="h-4 w-4 mr-2" />
      ) : (
        <BellOff className="h-4 w-4 mr-2" />
      )}
      {isSubscribed ? 'Notificações Ativas' : 'Ativar Notificações'}
    </Button>
  );
}

// Componente para enviar WhatsApp manualmente
export function WhatsAppButton({ alunoId, responsavelId, alunoNome, compact = false }) {
  const [isLoading, setIsLoading] = useState(false);

  const handleSendWhatsApp = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/dnago/notificacoes/${responsavelId}/whatsapp-link/${alunoId}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Erro ao gerar link do WhatsApp');
      }

      const data = await response.json();
      
      // Abrir WhatsApp em nova aba
      window.open(data.whatsapp_link, '_blank');
      
      toast.success(`WhatsApp aberto para notificar sobre ${alunoNome}`);
    } catch (error) {
      console.error('Erro:', error);
      toast.error('Erro ao gerar link do WhatsApp');
    } finally {
      setIsLoading(false);
    }
  };

  if (compact) {
    return (
      <Button
        variant="ghost"
        size="icon"
        onClick={handleSendWhatsApp}
        disabled={isLoading}
        className="h-8 w-8 text-green-600 hover:text-green-700 hover:bg-green-50"
        title="Enviar WhatsApp"
      >
        {isLoading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <MessageCircle className="h-4 w-4" />
        )}
      </Button>
    );
  }

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={handleSendWhatsApp}
      disabled={isLoading}
      className="text-green-600 border-green-600 hover:bg-green-50"
    >
      {isLoading ? (
        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
      ) : (
        <MessageCircle className="h-4 w-4 mr-2" />
      )}
      WhatsApp
    </Button>
  );
}

// Dialog para configurar notificações de um responsável
export function NotificationSettingsDialog({ responsavel, alunos = [] }) {
  const [open, setOpen] = useState(false);

  if (!responsavel) return null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <Bell className="h-4 w-4 mr-2" />
          Notificações
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Configurar Notificações</DialogTitle>
          <DialogDescription>
            Configure como {responsavel.name} receberá as notificações de chamada.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* Push Notifications */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Bell className="h-4 w-4" />
                Notificações Push
              </CardTitle>
              <CardDescription className="text-xs">
                Receba notificações instantâneas no navegador
              </CardDescription>
            </CardHeader>
            <CardContent>
              <PushNotificationButton 
                responsavelId={responsavel.id}
                responsavelNome={responsavel.name}
              />
            </CardContent>
          </Card>

          {/* WhatsApp */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <MessageCircle className="h-4 w-4 text-green-600" />
                WhatsApp
              </CardTitle>
              <CardDescription className="text-xs">
                Telefone: {responsavel.whatsapp || responsavel.cellphone || 'Não cadastrado'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {responsavel.whatsapp || responsavel.cellphone ? (
                <Badge variant="outline" className="text-green-600 border-green-600">
                  <Check className="h-3 w-3 mr-1" />
                  WhatsApp disponível
                </Badge>
              ) : (
                <Badge variant="outline" className="text-red-600 border-red-600">
                  <X className="h-3 w-3 mr-1" />
                  WhatsApp não cadastrado
                </Badge>
              )}
            </CardContent>
          </Card>

          {/* Lista de alunos vinculados */}
          {alunos.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Alunos Vinculados</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {alunos.map(aluno => (
                    <div key={aluno.id} className="flex items-center justify-between text-sm">
                      <span>{aluno.name}</span>
                      <Badge variant="secondary">{aluno.class_name}</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default { PushNotificationButton, WhatsAppButton, NotificationSettingsDialog };
